import { useCallback, useRef } from 'react';
import {
  createLogger,
  getImageScenesByGame,
  getImageScenePath,
  findGameName,
  getImageSceneById,
} from '@airhead-games/shared/utilities';
import {
  MediaReadyEvent,
  MediaActionType,
  MediaType,
  SenderRoleType,
} from '@airhead-games/shared/types';
import { useRoomSocket } from './useRoomSocket';
import { useRoomConfig } from './useRoomConfig';
import { generateCommandId } from '@airhead-games/shared/utilities';

const logger = createLogger('useMediaImageManager');

// Browser loading states for image URLs
type LoadingState = 'loading' | 'loaded' | 'error';

export function useMediaImageManager() {
  const loadingPromises = useRef<Record<string, Promise<void>>>({}); // Track loading promises to avoid duplicates
  const loadingStatesRef = useRef<Record<string, LoadingState>>({}); // Track loading states in a ref to avoid callback dependencies
  const cachedImageUrls = useRef<Record<string, string>>({}); // Cache for preloaded image scene URLs
  const pendingRequests = useRef<Record<string, (url: string) => void>>({}); // Track pending requests for image scenes

  // Get the sendCommand function from the room context and roomCode from the selector
  const { sendCommand } = useRoomSocket();
  const { roomCode } = useRoomConfig();

  // Requests an image scene URL from backend if not already cached
  const requestImageScene = useCallback(
    async (sceneId: string): Promise<string> => {
      // 1. Check cache
      if (cachedImageUrls.current[sceneId]) {
        return cachedImageUrls.current[sceneId];
      }

      // 2. Validate roomCode
      if (!roomCode) {
        throw new Error('Cannot request image scene: roomCode is missing');
      }

      // 3. Determine media type based on scene category
      const scene = getImageSceneById(sceneId);
      const mediaType =
        scene?.category === 'background'
          ? MediaType.BACKGROUND
          : MediaType.IMAGE;

      // 4. Issue backend request for image scene
      sendCommand({
        id: generateCommandId(),
        type: 'MediaElement',
        action: MediaActionType.REQUEST,
        mediaType,
        senderRole: SenderRoleType.HOST,
        clientTimestamp: Date.now(),
        roomCode,
        content: sceneId,
        imageUrl: getImageScenePath(sceneId),
      });

      // 5. Wait for response (signed URL will be provided by updateImageSceneWithSignedUrl)
      return new Promise<string>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          delete pendingRequests.current[sceneId];
          reject(new Error('Image scene request timeout'));
        }, 15000);

        pendingRequests.current[sceneId] = (url: string) => {
          clearTimeout(timeoutId);
          resolve(url);
        };
      });
    },
    [sendCommand, roomCode]
  );

  // Load all image scenes for a specific game
  const requestGameScene = useCallback(
    async (gameId: string): Promise<void> => {
      const gameScenes = getImageScenesByGame(gameId);

      // Filter to only uncached scenes
      const uncachedScenes = gameScenes.filter(
        (scene) => !cachedImageUrls.current[scene.id]
      );

      if (uncachedScenes.length === 0) {
        logger.debug(
          `All image scenes already loaded for ${findGameName(gameId)}, skipping requestGameScene`
        );
        return;
      }

      logger.debug(
        `Loading ${uncachedScenes.length} image scenes for ${findGameName(gameId)}...`
      );

      // Validate roomCode
      if (!roomCode) {
        throw new Error('Cannot request image scenes: roomCode is missing');
      }

      // Build items array for batched request (only uncached)
      const items = uncachedScenes.map((scene) => ({
        mediaType:
          scene.category === 'background'
            ? MediaType.BACKGROUND
            : MediaType.IMAGE,
        content: scene.id,
        imageUrl: getImageScenePath(scene.id),
      }));

      // Set up pending requests for uncached scenes only
      const loadPromises = uncachedScenes.map((scene) => {
        return new Promise<void>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            delete pendingRequests.current[scene.id];
            reject(new Error(`Image scene request timeout for ${scene.id}`));
          }, 15000);

          pendingRequests.current[scene.id] = () => {
            clearTimeout(timeoutId);
            resolve();
          };
        });
      });

      // Send single batched request (use first item's mediaType as required field)
      sendCommand({
        id: generateCommandId(),
        type: 'MediaElement',
        action: MediaActionType.REQUEST,
        mediaType: items[0].mediaType,
        senderRole: SenderRoleType.HOST,
        clientTimestamp: Date.now(),
        roomCode,
        content: uncachedScenes.map((scene) => scene.id).join(', '),
        items,
      });

      // Wait for all responses
      await Promise.allSettled(loadPromises);
      logger.debug(`Scenes loaded`);
    },
    [sendCommand, roomCode]
  );

  /**
   * Core image loading logic - shared between event-based and URL-based loading
   * @param imageUrl - The image URL to load
   * @param onSuccess - Callback when image loads successfully
   * @param onError - Callback when image fails to load
   */
  const loadImageCore = useCallback(
    (
      imageUrl: string,
      onSuccess: () => void,
      onError: (error: Error) => void
    ): Promise<void> => {
      // Check if already loaded (only check URL - same image can have different contexts)
      if (loadingStatesRef.current[imageUrl] === 'loaded') {
        logger.debug('Image already loaded, skipping');
        return Promise.resolve();
      }

      // Avoid duplicate loading requests
      const existingPromise = loadingPromises.current[imageUrl];
      if (existingPromise) {
        logger.debug('Image already loading, returning existing promise');
        return existingPromise;
      }

      // Set loading state
      loadingStatesRef.current[imageUrl] = 'loading';

      // Create loading promise
      const loadPromise = new Promise<void>((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          loadingStatesRef.current[imageUrl] = 'loaded';
          onSuccess();
          resolve();
        };

        img.onerror = () => {
          loadingStatesRef.current[imageUrl] = 'error';
          const errorMessage = `Failed to load image: ${imageUrl}`;
          onError(new Error(errorMessage));
          reject(new Error(errorMessage));
        };

        img.src = imageUrl;
      });

      // Store promise to avoid duplicates
      loadingPromises.current[imageUrl] = loadPromise;

      // Clean up promise when done
      loadPromise.finally(() => {
        delete loadingPromises.current[imageUrl];
      });

      return loadPromise;
    },
    []
  );

  /**
   * Load an image from a MediaReady event
   * @param event - The MediaReady event containing image data
   */
  const loadImageFromEvent = useCallback(
    (event: MediaReadyEvent): Promise<void> => {
      if (!event.imageUrl) {
        return Promise.reject(new Error('No imageUrl in MediaReady event'));
      }
      const sceneId = event.content;
      const { imageUrl } = event;
      logger.debug('Loading image from MediaReady event...', {
        mediaId: event.mediaId,
        mediaType: event.mediaType,
        imageUrl: event.imageUrl,
      });
      return loadImageCore(
        imageUrl,
        // onSuccess
        () => {
          logger.debug('Image loaded from MediaReady event');
          if (sceneId) {
            cachedImageUrls.current[sceneId] = imageUrl;

            const pendingCallback = pendingRequests.current[sceneId];
            if (pendingCallback) {
              pendingCallback(imageUrl);
              delete pendingRequests.current[sceneId];
            }
          }
        },
        // onError
        () => {
          logger.error('Failed to load image from MediaReady event');
        }
      );
    },
    [loadImageCore]
  );

  /**
   * Load any image URL (not from MediaReady event) - for static assets like game covers
   * @param imageUrl - The image URL to load
   * @returns Promise that resolves when image is loaded
   */
  const loadImageFromUrl = useCallback(
    (imageUrl: string): Promise<void> => {
      logger.debug('Loading image from url...', { imageUrl });
      return loadImageCore(
        imageUrl,
        // onSuccess
        () => {
          logger.debug('Image loaded from url');
        },
        // onError
        () => {
          logger.error('Failed to load image from url');
        }
      );
    },
    [loadImageCore]
  );

  // Update image scene with signed URL from centralized media manager
  const updateImageSceneWithSignedUrl = useCallback(
    (event: MediaReadyEvent) => {
      const { content: sceneId, imageUrl } = event;

      if (!sceneId || !imageUrl) {
        logger.warn(
          'updateImageSceneWithSignedUrl called without sceneId or imageUrl'
        );
        return;
      }

      // Cache the URL
      cachedImageUrls.current[sceneId] = imageUrl;

      // Resolve any pending requests for this image scene
      const pendingCallback = pendingRequests.current[sceneId];
      if (pendingCallback) {
        pendingCallback(imageUrl);
        delete pendingRequests.current[sceneId];
      }

      logger.debug(`Image scene loaded: ${sceneId}`);
    },
    []
  );

  // Flush all image manager state for room transitions
  const flushImageManager = useCallback(() => {
    logger.debug('Flushing image manager state...');
    // Clear all loading promises and states
    loadingPromises.current = {};
    loadingStatesRef.current = {};
    cachedImageUrls.current = {};
    pendingRequests.current = {};
    logger.debug('Image manager flushed');
  }, []);

  // Get cached image scene URL (equivalent to how SFX works)
  const getImageSceneItem = useCallback((sceneId: string): string | null => {
    return cachedImageUrls.current[sceneId] || null;
  }, []);

  return {
    loadImageFromEvent, // For MediaReady events (requires mediaId)
    loadImageFromUrl, // For static assets (no mediaId needed)
    requestImageScene, // For requesting individual image scenes
    requestGameScene, // For requesting all image scenes for a game
    getImageSceneItem, // For accessing cached image scene URLs
    updateImageSceneWithSignedUrl, // For handling signed URLs from backend
    flushImageManager, // For room transitions (clears all loading state)
  };
}
